import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { ChevronDownIcon, DocumentTextIcon, RectangleStackIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { sitePath } from "../../../utils/urls";
import { CHART_TOOLTIP, ROW_HOVER, RUNNABLE_TYPES, STATUS_META, TYPE_ICONS, TYPE_LABELS, VITAL_THRESHOLDS } from "./constants";
import {
  defaultSelectedHashes,
  formatReportDate,
  formatReportDateTime,
  formatVital,
  formatVolume,
  resolveAuditReport,
  VITAL_CODES,
  VITAL_SHORT,
} from "./utils";

/** Recharts tooltip with a solid theme background (contentStyle alone is often transparent). */
export const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  const when = point.finished_at
    ? formatReportDateTime(point.finished_at)
    : (label || "");
  return (
    <div className="rounded-lg px-2.5 py-2 text-xs shadow-lg" style={CHART_TOOLTIP}>
      {when && <p className="mb-1.5 text-theme-secondary-text">{when}</p>}
      <ul className="space-y-0.5">
        {payload.map((entry) => {
          const raw = entry.value;
          const display = VITAL_CODES.includes(entry.dataKey)
            ? formatVital(entry.dataKey, raw)
            : (entry.dataKey === "low" || entry.dataKey === "mid" || entry.dataKey === "high" || entry.dataKey === "searches")
              ? formatVolume(raw)
              : raw;
          return (
            <li key={entry.dataKey || entry.name} className="flex items-center gap-2 text-theme-text">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color || entry.payload?.fill }} />
              <span>{entry.name}</span>
              <span className="font-medium">{display}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const ReportTypeIcon = ({ type, className = "h-4 w-4" }) => {
  const Icon = TYPE_ICONS[type] || TYPE_ICONS.demand;
  return <Icon className={className} title={TYPE_LABELS[type] || type} />;
};

export const StatusDot = ({ status }) => {
  const active = status === "queued" || status === "running";
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${REPORT_OR_STATUS(status)} ${
        active ? "seo-status-blink" : ""
      }`}
    />
  );
};

const REPORT_OR_STATUS = (status) =>
  STATUS_META[status]?.dot
  || ({
    succeeded: "bg-emerald-500",
    failed: "bg-red-500",
    running: "bg-amber-500",
    queued: "bg-theme-shadow",
    canceled: "bg-theme-shadow",
  }[status] || "bg-theme-shadow");

export const StatusPill = ({ status }) => {
  const meta = STATUS_META[status];
  if (!meta) {
    return (
      <span className="rounded-full bg-theme-secondary px-2 py-0.5 text-[11px] capitalize text-theme-secondary-text">
        {status}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${meta.text}`}>
      <meta.Icon className="h-3.5 w-3.5" />
      {meta.label}
    </span>
  );
};

export const GradePill = ({ grade }) => <StatusPill status={grade} />;

/** Trailing › on clickable list rows. */
export const RowChevron = () => (
  <ChevronDownIcon className="h-4 w-4 shrink-0 -rotate-90 text-theme-secondary-text opacity-40" />
);

/** Optional pages + fail / warn / pass icon counts. Zero stays muted. */
export const StatusCounts = ({
  counts,
  pages,
  keys = ["fail", "warn", "pass"],
  labeled = false,
}) => (
  <div className="flex shrink-0 items-center gap-2.5">
    {pages != null && (
      <span
        className={`inline-flex items-center gap-0.5 text-xs ${
          pages > 0 ? "text-theme-text" : "text-theme-secondary-text"
        }`}
      >
        <RectangleStackIcon className="h-3.5 w-3.5" />
        {pages}
      </span>
    )}
    {keys.map((status) => {
      const meta = STATUS_META[status];
      const n = counts?.[status] || 0;
      const muted = n === 0;
      return (
        <span
          key={status}
          className={`inline-flex items-center gap-0.5 text-xs ${
            muted ? "text-theme-secondary-text" : meta.text
          }`}
        >
          <meta.Icon className="h-3.5 w-3.5" />
          {labeled ? `${n} ${meta.label.toLowerCase()}` : n}
        </span>
      );
    })}
  </div>
);

/** PSI-style good → needs improvement → poor gauge rows for Core Web Vitals. */
export const VitalsGauges = ({ vitals }) => {
  if (!vitals) return null;
  const rows = VITAL_CODES
    .map((code) => ({ code, value: vitals[code] }))
    .filter((row) => row.value != null && VITAL_THRESHOLDS[row.code]);
  if (!rows.length) return null;
  return (
    <div className="mt-3 space-y-2.5">
      {rows.map(({ code, value }) => {
        const [goodMax, needsMax] = VITAL_THRESHOLDS[code];
        const status = value <= goodMax ? "pass" : value <= needsMax ? "warn" : "fail";
        const meta = STATUS_META[status];
        const pct = vitalMarkerPercent(value, goodMax, needsMax);
        return (
          <div key={code}>
            <div className="mb-1 flex items-baseline justify-between gap-2">
              <span className="text-xs font-medium text-theme-text">{VITAL_SHORT[code]}</span>
              <span className={`text-xs font-semibold tabular-nums ${meta.text}`}>
                {formatVital(code, value)}
              </span>
            </div>
            <div className="relative h-1.5 overflow-hidden rounded-full">
              <div className="absolute inset-0 flex">
                <div className="w-1/3 bg-emerald-500/80" />
                <div className="w-1/3 bg-amber-500/80" />
                <div className="w-1/3 bg-red-500/80" />
              </div>
              <span
                className={`absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-theme-main shadow-sm ${meta.dot}`}
                style={{ left: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Map a vital into equal thirds: good | needs improvement | poor. */
function vitalMarkerPercent(value, goodMax, needsMax) {
  if (value <= goodMax) return (value / goodMax) * (100 / 3);
  if (value <= needsMax) {
    return (100 / 3) + ((value - goodMax) / (needsMax - goodMax)) * (100 / 3);
  }
  const over = Math.min(1, (value - needsMax) / needsMax);
  return Math.min(98, (200 / 3) + over * (100 / 3));
}

export const FilterChip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
      active
        ? "border-theme-primary bg-theme-secondary text-theme-primary"
        : "border-theme-secondary text-theme-secondary-text hover:text-theme-text"
    }`}
  >
    {children}
  </button>
);

/** Title left, optional count, filters right — stays inside the 2/3 column. */
export const PanelHeader = ({ title, count, filters }) => (
  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
    <div className="flex min-w-0 flex-wrap items-baseline gap-2">
      <h2 className="text-base font-semibold text-theme-text">{title}</h2>
      {count != null && (
        <span className="text-sm text-theme-secondary-text">({count})</span>
      )}
    </div>
    {filters && (
      <div className="flex flex-wrap items-center justify-end gap-2">{filters}</div>
    )}
  </div>
);

/** Always 2/3 main + 1/3 side (side may be empty for spacing). */
export const TwoColumn = ({ main, side }) => (
  <div className="grid gap-6 lg:grid-cols-3">
    <div className="min-w-0 lg:col-span-2">{main}</div>
    <div className="hidden min-w-0 space-y-3 lg:block">{side}</div>
  </div>
);

/** Stack of right-rail sections (no cards). Matches All Audits sidebar. */
export const SideStack = ({ children }) => (
  <div className="space-y-8">{children}</div>
);

/** One right-rail section: title (+ optional action) left, meta right, then content. */
export const SideBlock = ({ title, action, meta, children }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-2">
      <h3 className="truncate text-base font-semibold text-theme-text">{title}</h3>
      {action}
      {meta != null && (
        <div className="ml-auto flex shrink-0 items-center gap-2">{meta}</div>
      )}
    </div>
    {children}
  </div>
);

/** Shared empty state when the project has never run an SEO report. */
export const SeoEmptyState = ({
  selectedType,
  onSelectType,
  creating,
  onCreate,
}) => (
  <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-6 text-center">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-theme-secondary">
      <DocumentTextIcon className="h-6 w-6 text-theme-primary" />
    </div>
    <h2 className="text-lg font-semibold text-theme-text">No reports run yet</h2>
    <p className="mt-1.5 text-sm text-theme-secondary-text">
      Find SEO issues across your site and fix them with one click.
    </p>

    <ul className="mt-5 w-full space-y-1.5 text-left">
      {RUNNABLE_TYPES.map((item) => {
        const Icon = TYPE_ICONS[item.id] || DocumentTextIcon;
        const selected = selectedType === item.id;
        return (
          <li key={item.id}>
            <button
              type="button"
              disabled={item.disabled}
              onClick={() => !item.disabled && onSelectType(item.id)}
              className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition ${
                item.disabled
                  ? "cursor-not-allowed border-theme-secondary opacity-50"
                  : selected
                    ? "border-theme-primary bg-theme-secondary"
                    : "border-theme-secondary hover:bg-theme-secondary/50"
              }`}
            >
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
                selected ? "bg-theme-primary/15 text-theme-primary" : "bg-theme-secondary text-theme-text"
              }`}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="text-sm font-medium text-theme-text">{item.label}</span>
                  {item.note && (
                    <span className="text-[11px] text-theme-secondary-text">{item.note}</span>
                  )}
                </span>
                <span className="block truncate text-xs text-theme-secondary-text">
                  {item.description}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>

    <button
      type="button"
      disabled={creating || selectedType === "indexing"}
      onClick={() => onCreate(selectedType)}
      className="mt-5 w-full rounded-xl bg-theme-primary px-5 py-3 text-sm font-semibold text-theme-button disabled:opacity-40"
    >
      {creating ? "Starting…" : "Run your first report"}
    </button>
  </div>
);

/** "Issues from {link}" — only the audit name is clickable. */
export const AuditSourceTitle = ({ noun, project, reports, reportFilter, auditHash }) => {
  const audit = resolveAuditReport(reports, reportFilter, auditHash);
  if (!audit) return noun;
  const latest = audit.is_latest && !reportFilter;
  const typeLabel = (TYPE_LABELS[audit.type] || audit.type).toLowerCase();
  const linkText = latest
    ? `latest ${typeLabel}`
    : `${formatReportDate(audit.finished_at || audit.create_date)} ${typeLabel}`;
  return (
    <>
      {noun} from{" "}
      <Link
        to={sitePath(project, `seo/reports/${audit.hash}/`)}
        className="text-theme-primary hover:underline"
      >
        {linkText}
      </Link>
    </>
  );
};

/**
 * Compact popover to pick a past report (or "All latest").
 * value="" means all latest.
 */
export const ReportSelector = ({
  reports = [],
  value = "",
  onChange,
  allowAllLatest = true,
  typeFilter = null,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const options = reports.filter((r) => {
    if (r.status !== "succeeded") return false;
    if (typeFilter && r.type !== typeFilter) return false;
    return true;
  });
  const selected = options.find((r) => r.hash === value);
  const reportOptionLabel = (r) => {
    const type = TYPE_LABELS[r.type] || r.type;
    if (r.is_latest) return `${type} · latest`;
    const when = formatReportDate(r.finished_at || r.create_date);
    return when ? `${type} · ${when}` : type;
  };
  const label = selected
    ? reportOptionLabel(selected)
    : allowAllLatest
      ? "All latest"
      : "Select report";

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const rowClass = (active) =>
    `flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-theme-secondary/50 ${
      active ? "bg-theme-secondary" : ""
    }`;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full border border-theme-secondary px-2.5 py-1 text-xs font-medium text-theme-text hover:bg-theme-secondary/50"
      >
        {selected && <ReportTypeIcon type={selected.type} className="h-3.5 w-3.5" />}
        <span className="max-w-[14rem] truncate">{label}</span>
        <ChevronDownIcon className={`h-3.5 w-3.5 text-theme-secondary-text transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1.5 w-72 overflow-hidden rounded-xl border border-theme-secondary bg-theme-main shadow-xl">
          <ul className="max-h-64 overflow-y-auto py-1">
            {allowAllLatest && (
              <li>
                <button
                  type="button"
                  onClick={() => { onChange(""); setOpen(false); }}
                  className={rowClass(!value)}
                >
                  <RectangleStackIcon className="h-4 w-4 shrink-0 text-theme-secondary-text" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">All latest reports</p>
                    <p className="truncate text-[11px] text-theme-secondary-text">Latest of each type</p>
                  </div>
                </button>
              </li>
            )}
            {options.map((r) => (
              <li key={r.hash}>
                <button
                  type="button"
                  onClick={() => { onChange(r.hash); setOpen(false); }}
                  className={rowClass(value === r.hash)}
                >
                  <ReportTypeIcon type={r.type} className="h-4 w-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{reportOptionLabel(r)}</p>
                    <p className="truncate text-[11px] text-theme-secondary-text">
                      {r.headline?.display || "Succeeded"}
                    </p>
                  </div>
                </button>
              </li>
            ))}
            {!options.length && (
              <li className="px-3 py-4 text-center text-xs text-theme-secondary-text">
                No succeeded reports yet.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export const Drawer = ({ title, subtitle, onClose, children, footer }) => (
  <div className="fixed inset-0 z-50 flex justify-end">
    <button
      type="button"
      className="absolute inset-0 cursor-default bg-black/40"
      onClick={onClose}
      aria-label="Close drawer"
    />
    <aside className="relative z-10 flex h-full w-full max-w-lg flex-col border-l border-theme-secondary bg-theme-main shadow-xl">
      <header className="flex items-start justify-between gap-3 border-b border-theme-secondary px-5 py-4">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-theme-text">{title}</h2>
          {subtitle && (
            <p className="mt-0.5 truncate text-xs text-theme-secondary-text">{subtitle}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-theme-secondary-text hover:bg-theme-secondary hover:text-theme-text"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      {footer && (
        <footer className="border-t border-theme-secondary px-5 py-3">{footer}</footer>
      )}
    </aside>
  </div>
);

export const GradeDonut = ({ counts, center, caption, compact = false }) => {
  const rows = ["pass", "warn", "fail"]
    .map((key) => ({ name: STATUS_META[key].label, value: counts[key] || 0, fill: STATUS_META[key].fill }))
    .filter((row) => row.value > 0);
  if (!rows.length) return null;
  return (
    <div className={`flex items-center ${compact ? "gap-2" : "gap-4"}`}>
      <div className={`relative ${compact ? "h-14 w-14" : "h-36 w-36"}`}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              dataKey="value"
              nameKey="name"
              innerRadius={compact ? 16 : 44}
              outerRadius={compact ? 26 : 64}
              stroke="none"
              paddingAngle={2}
            >
              {rows.map((row) => (
                <Cell key={row.name} fill={row.fill} />
              ))}
            </Pie>
            {!compact && <Tooltip content={<ChartTooltip />} />}
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-semibold ${compact ? "text-[11px]" : "text-lg"}`}>{center}</span>
          {!compact && (
            <span className="text-[10px] uppercase text-theme-secondary-text">{caption}</span>
          )}
        </div>
      </div>
      {!compact && (
        <ul className="space-y-1 text-xs">
          {rows.map((row) => (
            <li key={row.name} className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: row.fill }} />
              <span>{row.name}</span>
              <span className="text-theme-secondary-text">{row.value}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Compact donut for table rows (replaces the thin progress bar). */
export const MiniDonut = ({ counts }) => {
  const total = (counts.pass || 0) + (counts.warn || 0) + (counts.fail || 0) + (counts.skipped || 0);
  if (!total) return null;
  const issues = (counts.fail || 0) + (counts.warn || 0);
  return <GradeDonut counts={counts} center={issues || total} caption="" compact />;
};

export const CheckMeter = ({ counts, wide = false }) => {
  const total = counts.pass + counts.warn + counts.fail + counts.skipped;
  if (!total) return null;
  return (
    <div className={`flex items-center gap-2 ${wide ? "w-full" : "w-28 shrink-0"}`}>
      <div className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-theme-secondary">
        {["fail", "warn", "skipped", "pass"].map((status) => (
          counts[status] > 0 && (
            <div
              key={status}
              className={STATUS_META[status].dot}
              style={{ width: `${(counts[status] / total) * 100}%` }}
            />
          )
        ))}
      </div>
      {!wide && counts.fail + counts.warn > 0 && (
        <span className="w-8 text-right text-[11px] text-theme-secondary-text">
          {counts.fail + counts.warn}
        </span>
      )}
    </div>
  );
};

export function useFixSelection(items, actionable) {
  // Stabilize on content, not array identity (parents often pass a fresh .map() each render).
  const key = (items || [])
    .map((item) => `${item.hash}:${item.severity}:${item.status}:${item.agent_type}`)
    .join("|");

  const fixable = useMemo(
    () => (items || []).filter(
      (item) => item.hash
        && item.agent_type === "coding"
        && item.status === "open"
        && (item.severity === "fail" || item.severity === "warn"),
    ),
    // ponytail: key covers the fields we care about; items is intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );
  const [selected, setSelected] = useState(() => defaultSelectedHashes(fixable));

  useEffect(() => {
    setSelected(defaultSelectedHashes(fixable));
  }, [key, fixable]);

  const toggle = (hash, severity) => {
    if (!actionable || !hash) return;
    if (severity !== "fail" && severity !== "warn") return;
    setSelected((prev) => (
      prev.includes(hash) ? prev.filter((h) => h !== hash) : [...prev, hash]
    ));
  };

  return { fixable, selected, toggle, canFix: actionable && selected.length > 0 };
}

export const FixCheckboxRow = ({
  item,
  checked,
  actionable,
  onToggle,
  children,
}) => {
  const selectable = actionable
    && item.hash
    && item.agent_type === "coding"
    && item.status === "open"
    && (item.severity === "fail" || item.severity === "warn");
  return (
    <label
      className={`flex items-start gap-3 rounded-lg border border-theme-secondary px-3 py-2 ${
        selectable ? `cursor-pointer ${ROW_HOVER}` : "opacity-70"
      }`}
    >
      <input
        type="checkbox"
        className="mt-1"
        checked={!!checked}
        disabled={!selectable}
        onChange={() => onToggle(item.hash, item.severity)}
      />
      <div className="min-w-0 flex-1">{children}</div>
    </label>
  );
};

export const FixFooter = ({ canFix, count, onFix, actionable, leading = null }) => {
  if (!actionable) {
    return (
      <div className="flex items-center gap-2">
        {leading}
        <p className="min-w-0 flex-1 text-xs text-theme-secondary-text">
          Viewing an older report. Switch to the latest to create fixes.
        </p>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {leading}
      <button
        type="button"
        disabled={!canFix}
        onClick={onFix}
        className="min-w-0 flex-1 rounded-lg bg-theme-primary px-3 py-2 text-sm font-medium text-theme-button disabled:opacity-40"
      >
        Fix selected {count > 0 ? `(${count})` : "pages"}
      </button>
    </div>
  );
};
